import { BRANDS } from 'contexts/CruiseSearchContext/constants';
import { getCache, getCacheKey, setCache } from 'lib/cache';
import { createApolloClient } from 'lib/graphql/apolloClient';
import { Locale, LocaleQueryVariables, getLocale } from 'lib/graphql/globalization/globalization.client';
import { logError, logInfo } from 'lib/utilities/logger';
import { BrandCode } from 'types';
import {
	CEL_INTERNATIONAL_MARKET,
	DEFAULT_COUNTRY,
	DEFAULT_COUNTRY_ALPHA2_CODE,
	DEFAULT_CURRENCIES,
	DEFAULT_CURRENCY,
	DEFAULT_LANGUAGE,
	DEFAULT_LOCALE,
	DEFAULT_LOCALE_CODE,
	DEFAULT_MARKET,
	DEFAULT_NAME_COUNTRY,
	DEFAULT_NAME_PHONE_NUMBER,
	DEFAULT_OFFICE,
	DEFAULT_PATH,
	ISO_ALPHA_2_CODE_LENGTH,
} from './Constants';

export interface LocaleData {
	country: string;
	currency: string;
	language: string;
	market: string;
	office: string;
	nameCountry: string;
	phoneNumber: string;
	currencies: string[];
	path: string;
	code: string;
	countryAlpha2Code: string;
}

const createLocale = (locale: Locale, currencyCode: string): LocaleData => {
	const [phoneNumber] = Array.isArray(locale?.country.phoneNumber) ? locale.country.phoneNumber : [];
	const currencies = locale?.country?.currencies?.filter((x) => x !== null && x !== undefined)?.map((x) => x.code);
	const localePath = getLocalePath(locale);
	let currency = locale?.currency?.code;

	if (currencyCode && currencies.some((item) => item === currencyCode)) {
		currency = currencyCode;
	}

	return {
		country: locale?.country.code || DEFAULT_COUNTRY,
		currency: currency || DEFAULT_CURRENCY,
		language: locale?.language?.code || DEFAULT_LANGUAGE,
		market: locale?.market?.code || DEFAULT_MARKET,
		office: locale?.country?.office?.code || DEFAULT_OFFICE,
		nameCountry: locale?.country.name || DEFAULT_NAME_COUNTRY,
		phoneNumber: phoneNumber || DEFAULT_NAME_PHONE_NUMBER,
		currencies: currencies || DEFAULT_CURRENCIES,
		path: localePath || DEFAULT_PATH,
		code: locale?.code || DEFAULT_LOCALE_CODE,
		countryAlpha2Code: locale?.country.isoAlpha2Code || DEFAULT_COUNTRY_ALPHA2_CODE,
	};
};

const getLocalePath = (locale) => {
	const { path } = locale;

	if (!path) {
		return null;
	}

	if (path.endsWith('/')) {
		return locale?.path;
	}

	return `${path}/`;
};

const getBrandFallbackCountry = (query, cookies, brandCode) => {
	if (brandCode === BrandCode.Celebrity) {
		if (!query?.market) {
			return DEFAULT_COUNTRY;
		}

		if (query?.market === CEL_INTERNATIONAL_MARKET) {
			return cookies?.wuc;
		}

		return null;
	}

	const { country } = cookies;
	let parsedCountry = null;

	try {
		parsedCountry = JSON.parse(country);
	} catch {
		parsedCountry = country;
	}

	return Array.isArray(parsedCountry) ? parsedCountry[0] : parsedCountry;
};

export const loadLocale = async (host, query, cookies, config, logId, envGraphColor): Promise<LocaleData> => {
	let locale = DEFAULT_LOCALE;

	try {
		const { brand } = config;
		const brandSettings = BRANDS.find((b) => b.code === brand.code);

		if (brandSettings?.g11n) {
			const parsedCountry = Array.isArray(query?.country) ? query?.country[0] : query?.country;
			const country = parsedCountry || getBrandFallbackCountry(query, cookies, brand.code);
			const market = query?.market;
			const language = query?.language;
			const currencyCode = query?.currency || query?.currencyCode;
			const cacheKey = getCacheKey('locale', [brand.code, country, market, language, currencyCode]);
			const cachedData = getCache<LocaleData>(cacheKey);

			if (cachedData) {
				logInfo('[Server Cache] Locale Cache Hit', { cacheKey, logId, cachedData });

				return cachedData;
			}

			logInfo('[Server Cache] Locale Cache Miss', { cacheKey, logId });

			const apolloClient = createApolloClient({ config, host, logId, envGraphColor, locale: undefined });

			const variables: LocaleQueryVariables = {};
			if (market) {
				variables[market.length === ISO_ALPHA_2_CODE_LENGTH ? 'isoAlpha2Code' : 'market'] = market;
			}
			if (language) {
				variables.language = language;
			}
			if (country) {
				variables.country = country;
			}

			const { data, error } = await getLocale(apolloClient, variables);

			if (error) {
				logError('getLocale error', { error, logId });
			}
			if (data?.locale) {
				locale = createLocale(data.locale, currencyCode);
				setCache(cacheKey, locale);

				const localeCacheKey = getCacheKey('locale', [brand.code, locale.country, locale.market, locale.language, locale.currency]);
				if (localeCacheKey !== cacheKey) {
					setCache(localeCacheKey, locale);
				}
			}
		}
	} catch (error) {
		logError('loadLocale error', { error, logId });
	}

	return locale;
};
